import { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';

function fmt$(n) {
  const v = parseFloat(n ?? 0);
  return v === Math.round(v)
    ? '$' + Math.round(v).toLocaleString()
    : '$' + v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function PublicPoRequest({ token }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted]   = useState(false);
  const [submitErr, setSubmitErr]   = useState('');

  useEffect(() => {
    if (!token) { setError('Invalid link.'); setLoading(false); return; }
    supabase.functions.invoke('get-public-po-request', { body: { token } })
      .then(({ data: res, error: fnErr }) => {
        if (fnErr || !res?.ok) { setError('This link is not valid or has expired.'); }
        else { setData(res); if (res.alreadyFulfilled) setSubmitted(true); }
        setLoading(false);
      });
  }, [token]);

  const submit = async () => {
    if (!poNumber.trim()) { setSubmitErr('Please enter a PO number.'); return; }
    setSubmitting(true); setSubmitErr('');
    const { data: res, error: fnErr } = await supabase.functions.invoke('submit-po-request', {
      body: { token, po_number: poNumber.trim() },
    });
    setSubmitting(false);
    if (fnErr || !res?.ok) { setSubmitErr('Something went wrong — please try again.'); return; }
    setSubmitted(true);
  };

  const page = (children) => (
    <div style={{ minHeight: '100vh', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Arial, Helvetica, sans-serif', padding: '20px' }}>
      <div style={{ width: 'min(440px, 100%)', background: '#fff', borderRadius: '10px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        {children}
      </div>
    </div>
  );

  if (loading) return page(
    <div style={{ padding: '48px 32px', textAlign: 'center', color: '#64748b', fontSize: '14px' }}>Loading…</div>
  );

  if (error) return page(
    <div style={{ padding: '48px 32px', textAlign: 'center' }}>
      <div style={{ fontSize: '32px', marginBottom: '12px' }}>🔒</div>
      <div style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b', marginBottom: '6px' }}>Link Not Found</div>
      <div style={{ fontSize: '13px', color: '#64748b' }}>{error}</div>
    </div>
  );

  const headerLogo = data.orgLogo
    ? <img src={data.orgLogo} alt={data.orgName} style={{ maxHeight: '44px', maxWidth: '160px', display: 'block', marginBottom: '8px' }} />
    : null;

  const periodLabel = data.period
    ? new Date(data.period + '-02').toLocaleString('en-US', { month: 'long', year: 'numeric' })
    : '';

  // The client's own chosen brand colors, not TimeKeep's.
  const brandBg = data.orgPrimaryColor || '#111D39';
  const brandAccent = data.orgAccentColor || '#00A0C8';

  if (submitted) return page(
    <>
      <div style={{ background: brandBg, padding: '20px 32px' }}>
        {headerLogo}
        <div style={{ color: brandAccent, fontSize: '16px', fontWeight: '800' }}>{data.orgName}</div>
      </div>
      <div style={{ padding: '40px 32px', textAlign: 'center' }}>
        <div style={{ fontSize: '32px', marginBottom: '12px' }}>✓</div>
        <div style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b', marginBottom: '8px' }}>Thank you!</div>
        <div style={{ fontSize: '13px', color: '#64748b', lineHeight: '1.6' }}>
          {data.poNumber ? <>PO <strong>{data.poNumber}</strong> has been recorded and the invoice is on its way to your inbox.</>
            : 'The invoice is on its way to your inbox.'}
        </div>
      </div>
    </>
  );

  return page(
    <>
      <div style={{ background: brandBg, padding: '20px 32px' }}>
        {headerLogo}
        <div style={{ color: brandAccent, fontSize: '16px', fontWeight: '800' }}>{data.orgName}</div>
        <div style={{ color: '#94a3b8', fontSize: '12px', marginTop: '2px' }}>PO Number Needed</div>
      </div>
      <div style={{ padding: '32px' }}>
        <div style={{ fontSize: '13px', color: '#475569', marginBottom: '20px', lineHeight: '1.6' }}>
          Invoice <strong>{data.invoiceNumber}</strong>{periodLabel && ` for ${periodLabel}`} from <strong>{data.orgName}</strong> to <strong>{data.clientName}</strong> is ready to send. Enter your PO number below and the invoice will be emailed to you right away.
        </div>
        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '20px', textAlign: 'center', marginBottom: '24px' }}>
          <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>Invoice Total</div>
          <div style={{ fontSize: '30px', fontWeight: '800', color: '#1e293b' }}>{fmt$(data.total)}</div>
        </div>
        <div style={{ marginBottom: '8px' }}>
          <label style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>PO Number</label>
          <input
            type="text" autoFocus value={poNumber}
            onChange={e => { setPoNumber(e.target.value); setSubmitErr(''); }}
            onKeyDown={e => e.key === 'Enter' && submit()}
            placeholder="e.g. PO-12345"
            style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', fontSize: '14px', border: '1px solid #cbd5e1', borderRadius: '6px', outline: 'none' }}
          />
        </div>
        {submitErr && <div style={{ fontSize: '12px', color: '#dc2626', marginBottom: '12px' }}>{submitErr}</div>}
        <button
          onClick={submit} disabled={submitting}
          style={{ width: '100%', padding: '12px', marginTop: '8px', background: brandBg, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '700', cursor: submitting ? 'default' : 'pointer', opacity: submitting ? 0.7 : 1 }}
        >
          {submitting ? 'Submitting…' : 'Submit & Send Invoice'}
        </button>
      </div>
    </>
  );
}
